import { useChatMessages, useChatStatus } from '@ai-sdk-tools/store';
import type { ChatStatus } from 'ai';
import { useEffect, useRef } from 'react';

import type { UIMessageWithMetadata } from '../../../lib/types';

export interface UseAutoResumeParams {
  /**
   * Whether auto-resume is enabled.
   * When true, the hook will automatically resume interrupted streams.
   */
  autoResume: boolean;
  /**
   * The initial messages for the chat.
   * Used to check whether the chat should reconnect to an interrupted stream.
   */
  initialMessages: UIMessageWithMetadata[];
  /**
   * Whether the backend still marks this chat as having an active stream.
   * This is important for tool-driven continuations, where the last persisted
   * message can still belong to the assistant.
   */
  hasActiveStream: boolean;
  /**
   * Backend stream id for the active stream, if known.
   * Used to avoid repeatedly probing the same stale stream.
   */
  activeStreamId?: string | null;
  /**
   * Function to resume the stream.
   * Should be called when auto-resume is enabled and the chat appears interrupted.
   */
  resumeStream: () => void;
  /**
   * Called immediately before a resume probe starts.
   * Used by the transport layer to mark 204 responses as unavailable.
   */
  onResumeAttempt?: (attemptKey: string) => void;
}

const unavailableAutoResumeAttemptKeys = new Set<string>();

export function markAutoResumeAttemptUnavailable(attemptKey: string): void {
  unavailableAutoResumeAttemptKeys.add(attemptKey);
}

export function clearUnavailableAutoResumeAttempts(): void {
  unavailableAutoResumeAttemptKeys.clear();
}

export function isAutoResumeAttemptUnavailable(attemptKey: string): boolean {
  return unavailableAutoResumeAttemptKeys.has(attemptKey);
}

export function shouldAutoResume({
  hasActiveStream,
  messages,
  status,
}: {
  hasActiveStream: boolean;
  messages: UIMessageWithMetadata[];
  status: ChatStatus;
}): boolean {
  if (status !== 'ready') {
    return false;
  }

  if (hasActiveStream) {
    return true;
  }

  return messages.at(-1)?.role === 'user';
}

export function getAutoResumeAttemptKey({
  activeStreamId,
  hasActiveStream,
  messages,
}: {
  activeStreamId?: string | null;
  hasActiveStream: boolean;
  messages: UIMessageWithMetadata[];
}): string | null {
  const lastMessage = messages.at(-1);

  if (hasActiveStream) {
    return `stream:${activeStreamId ?? lastMessage?.id ?? messages.length}`;
  }

  if (lastMessage?.role === 'user') {
    return `user:${lastMessage.id}`;
  }

  return null;
}

/**
 * Hook that automatically resumes interrupted chat streams.
 * Based on the Vercel AI Chatbot implementation: https://github.com/vercel/ai-chatbot/blob/d0b6f37aeee532b942245ee9d406312f79e2fabe/hooks/use-auto-resume.ts
 *
 * When autoResume is enabled and the chat still looks interrupted
 * (either because the backend reports an active stream or because the
 * last persisted message is still a user turn), this hook will
 * automatically call resumeStream().
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const chat = useChat({ resume: true, ... });
 *   const { resumeStream } = chat; // or from useChatActions
 *
 *   useAutoResume({
 *     autoResume: true,
 *     initialMessages: initialMessages,
 *     hasActiveStream: false,
 *     resumeStream,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAutoResume({
  autoResume,
  initialMessages,
  hasActiveStream,
  activeStreamId,
  resumeStream,
  onResumeAttempt,
}: UseAutoResumeParams) {
  // Use messages from the store to check current state
  // This ensures we're checking against the actual current messages
  const storeMessages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const attemptedResumeKeyRef = useRef<string | null>(null);

  const messagesToCheck = storeMessages.length > 0 ? storeMessages : initialMessages;
  const attemptKey = getAutoResumeAttemptKey({
    activeStreamId,
    hasActiveStream,
    messages: messagesToCheck,
  });

  useEffect(() => {
    if (!autoResume) {
      return;
    }

    if (
      shouldAutoResume({
        hasActiveStream,
        messages: messagesToCheck,
        status,
      })
    ) {
      if (!attemptKey || attemptedResumeKeyRef.current === attemptKey || isAutoResumeAttemptUnavailable(attemptKey)) {
        return;
      }

      attemptedResumeKeyRef.current = attemptKey;
      onResumeAttempt?.(attemptKey);
      resumeStream();
    }

    // We intentionally run this when the resumable candidate changes or when autoResume/resumeStream changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptKey, autoResume, hasActiveStream, onResumeAttempt, resumeStream, status]);
}
